import { Textarea } from '@/components/ui/textarea';
import { notify, getApiErrorMessage } from '@/lib/toast';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Loader2, ScanBarcode, FlaskConical, CheckCircle, XCircle, ExternalLink, Package, Warehouse, ChefHat, Receipt, Download, Layers, Hash, ShieldOff, List, SlidersHorizontal, RotateCcw, Save } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { BarcodeDisplay } from '@/components/ui/barcode-display';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { SearchInput } from '@/components/ui/search-input';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { PageHeader } from '@/components/ui/page-header';
import { TabBar } from '@/components/ui/tab-bar';
import { AlertBanner } from '@/components/ui/alert-banner';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';
import { ProductSearchCombobox } from '@/components/inventory/ProductSearchCombobox';
import { Link } from 'react-router-dom';
import { TAX_RATES_PATH } from '@/lib/tax';


/** FBR UOM static list — confirmed valid by FBR DI API */
const FBR_UOM_OPTIONS = [
  { code: 'Pcs', label: 'Pcs – Pieces' },
  { code: 'KG', label: 'KG – Kilograms' },
  { code: 'Gram', label: 'Gram – Grams' },
  { code: 'MT', label: 'MT – Metric Tonnes' },
  { code: 'Liter', label: 'Liter – Litres' },
  { code: 'Meter', label: 'Meter – Metres' },
  { code: 'KWH', label: 'KWH – Kilowatt Hours' },
  { code: 'MMBTU', label: 'MMBTU' },
  { code: 'Bag', label: 'Bag – Bags' },
  { code: 'SET', label: 'SET – Sets' },
  { code: 'Dozen', label: 'Dozen – Dozens' },
  { code: 'Pair', label: 'Pair – Pairs' },
  { code: 'Barrels', label: 'Barrels' },
  { code: 'Packs', label: 'Packs' },
  { code: 'Square Metre', label: 'Square Metre' },
  { code: 'Square Foot', label: 'Square Foot' },
  { code: 'Numbers, pieces, units', label: 'Numbers, pieces, units' },
  { code: 'Thousand Unit', label: 'Thousand Unit' },
  { code: 'Others', label: 'Others' },
];

/** Tax assignment on a product row (`taxRate` is included by the products list API). */
interface ProductTaxLink { taxRateId: string; id?: string; name?: string; taxRate: { id: string; name: string } }
/** Product row as returned by GET /products (admin catalog list). */
interface ProductRow {
  id: string;
  name: string;
  sku: string | null;
  barcode?: string | null;
  categoryId?: string | null;
  brandId?: string | null;
  model?: string | null;
  costPrice?: number | string | null;
  salePrice?: number | string | null;
  cashPrice?: number | string | null;
  installmentPrice?: number | string | null;
  minimumPrice?: number | string | null;
  markupPercent?: number | string | null;
  stockQuantity: number;
  unit?: string | null;
  trackStock?: boolean;
  serialTracking?: boolean;
  expiryTracking?: boolean;
  shelfLifeDays?: number | null;
  taxOverride?: boolean;
  /** Legacy single tax rate (CSV import / older versions); charged when no product or category rate applies. */
  taxRate?: number | string | null;
  taxes: ProductTaxLink[];
  hsCode?: string | null;
  uom?: string | null;
  skipPaymentMethodTax?: boolean;
  lowStockAlert?: number | null;
  reorderPoint?: number | string | null;
  reorderQuantity?: number | string | null;
  leadTimeDays?: number | string | null;
  productType?: 'REGULAR' | 'RAW_MATERIAL' | 'SEMI_FINISHED';
  imageUrl?: string | null;
  isBundle?: boolean;
  category?: { name: string } | null;
}
/** Tax link on a category row (nested taxRate may be partial). */
interface CategoryTaxLink { taxRateId?: string; taxRate?: { id?: string; name?: string; rate?: number; type?: string } }
interface CategoryRow { id: string; name: string; taxes?: CategoryTaxLink[] }
interface BrandRow { id: string; name: string }
interface TaxRateRow { id: string; name: string; rate: number; type: string; isActive?: boolean }
interface BundleComponentRow {
  id?: string;
  componentId: string;
  quantity: number;
  name?: string;
  salePrice?: number | string | null;
  component?: { name?: string; salePrice?: number | string | null };
}
interface SerialNumberRow { id: string; serialNo: string; status: string; notes?: string | null; createdAt: string }
/** Create/update body for POST/PATCH /products. */
interface ProductSavePayload {
  name: string;
  sku?: string;
  categoryId?: string;
  brandId?: string;
  model?: string;
  costPrice?: number;
  salePrice: number;
  cashPrice?: number;
  installmentPrice?: number;
  minimumPrice?: number;
  markupPercent?: number;
  stockQuantity?: number;
  confirmLargeValue?: boolean;
  unit: string;
  trackStock: boolean;
  serialTracking: boolean;
  expiryTracking: boolean;
  shelfLifeDays?: number;
  barcode?: string;
  taxOverride: boolean;
  taxRateIds?: string[];
  taxRate?: number;
  hsCode?: string;
  uom: string;
  skipPaymentMethodTax: boolean;
  lowStockAlert?: number;
  reorderPoint?: number;
  reorderQuantity?: number;
  leadTimeDays?: number;
  productType: 'REGULAR' | 'RAW_MATERIAL' | 'SEMI_FINISHED';
  imageUrl?: string;
  serialNumbers?: string[];
}

const defaultForm = {
  name: '',
  sku: '',
  categoryId: '',
  brandId: '',
  model: '',
  costPrice: '',
  salePrice: '',
  cashPrice: '',
  installmentPrice: '',
  minimumPrice: '',
  markupPercent: '',
  stockQuantity: '',
  unit: 'pcs',
  trackStock: true,
  serialTracking: false,
  expiryTracking: false,
  shelfLifeDays: '',
  barcode: '',
  taxOverride: false,
  taxRateIds: [] as string[],
  legacyTaxRate: 0,
  clearLegacyTax: false,
  hsCode: '',
  uom: 'Pcs',
  skipPaymentMethodTax: false,
  lowStockAlert: '5',
  reorderPoint: '',
  reorderQuantity: '',
  leadTimeDays: '',
  productType: 'REGULAR' as 'REGULAR' | 'RAW_MATERIAL' | 'SEMI_FINISHED',
  imageUrl: '',
};

export function ProductsPage() {
  const { t } = useTranslation('products');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<ProductRow | null>(null);
  const [bulkPriceModalOpen, setBulkPriceModalOpen] = useState(false);
  const [bulkPriceRows, setBulkPriceRows] = useState<Array<{ id: string; name: string; sku: string; costPrice: number; salePrice: number; newSalePrice: string }>>([]);
  const [form, setForm] = useState({ ...defaultForm });
  const [initialSerials, setInitialSerials] = useState(''); // for serial-tracked new products
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({ open: false, id: '', name: '' });
  const [hsSearchTerm, setHsSearchTerm] = useState('');
  const [hsResults, setHsResults] = useState<Array<{ code: string; description: string; category: string }>>([]);
  const [hsDropdownOpen, setHsDropdownOpen] = useState(false);
  const hsDropdownRef = useRef<HTMLDivElement>(null);
  const [hsLookupLoading, setHsLookupLoading] = useState(false);
  const [hsLookupResult, setHsLookupResult] = useState<{ valid: boolean; isStandard18: boolean; validUoms: string[]; description: string | null; message: string } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'products' | 'bundles'>('products');
  const [formTab, setFormTab] = useState<'basics' | 'pricing' | 'stock' | 'fbr'>('basics');
  // POS-I237: opening stock is booked to Inventory; Rs 100,000 or more needs a confirmation
  const { t: tInv } = useTranslation('inventory');
  const [confirmOpening, setConfirmOpening] = useState(false);
  const openingValue = Math.round(Number(form.stockQuantity || 0) * Number(form.costPrice || 0) * 100) / 100;
  const openingNeedsConfirm = !editProduct && form.trackStock && openingValue >= 100_000;
  useEffect(() => { setConfirmOpening(false); }, [openingValue, dialogOpen]);
  // T-112: ConfirmDialog state for disabling stock tracking on a product with on-hand inventory.
  const [disableTrackStockConfirm, setDisableTrackStockConfirm] = useState<{
    open: boolean;
    stockQuantity: number;
  }>({ open: false, stockQuantity: 0 });
  const [bundleProduct, setBundleProduct] = useState<ProductRow | null>(null);
  const [bundleComponentId, setBundleComponentId] = useState('');
  const [bundleQty, setBundleQty] = useState('1');
  const [bundleSearch, setBundleSearch] = useState('');
  const [bundleView, setBundleView] = useState<'all' | 'bundles'>('all');
  const [stockFilter, setStockFilter] = useState<'all' | 'low-stock' | 'out-of-stock'>('all');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [serialViewProduct, setSerialViewProduct] = useState<ProductRow | null>(null);
  const [serialStatusFilter, setSerialStatusFilter] = useState('');
  const [serialSearch, setSerialSearch] = useState('');
  const [serialPage, setSerialPage] = useState(1);
  const queryClient = useQueryClient();
  const handleStockUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['products'] });
    queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
    queryClient.invalidateQueries({ queryKey: ['products-pos'] });
  }, [queryClient]);
  useSocketEvent('stock.updated', handleStockUpdate);
  useSocketEvent('purchase.received', handleStockUpdate);
  const { hasPermission } = useAuthStore();
  const canOpenTaxRates = hasPermission('tax-rates:read') || hasPermission('tax-rates:*');
  // products:* is the base-plan permission for catalog management.
  // inventory:* is the addon permission for stock operations.
  const canCreate = hasPermission('products:create') || hasPermission('products:*') || hasPermission('inventory:create') || hasPermission('inventory:*');
  const canUpdate = hasPermission('products:update') || hasPermission('products:*') || hasPermission('inventory:update') || hasPermission('inventory:*');
  const canDelete = hasPermission('products:delete') || hasPermission('products:*') || hasPermission('inventory:delete') || hasPermission('inventory:*');
  const canExport = hasPermission('products:read') || hasPermission('products:*') || hasPermission('inventory:read') || hasPermission('inventory:*');
  const canManageInventory = hasPermission('inventory:*') || hasPermission('purchases:*');
  const canFbrLookup = hasPermission('fbr:read') || hasPermission('fbr:*');

  const { data: tenantData } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
    staleTime: 30_000,
  });
  const isTestMode: boolean = tenantData?.testMode ?? false;

  useEffect(() => {
    if (dialogOpen && editProduct) {
      setForm({
        name: editProduct.name ?? '',
        sku: editProduct.sku ?? '',
        categoryId: editProduct.categoryId ?? '',
        brandId: editProduct.brandId ?? '',
        model: editProduct.model ?? '',
        costPrice: editProduct.costPrice != null ? String(editProduct.costPrice) : '',
        salePrice: editProduct.salePrice != null ? String(editProduct.salePrice) : '',
        cashPrice: editProduct.cashPrice != null ? String(editProduct.cashPrice) : '',
        installmentPrice: editProduct.installmentPrice != null ? String(editProduct.installmentPrice) : '',
        minimumPrice: editProduct.minimumPrice != null ? String(editProduct.minimumPrice) : '',
        markupPercent: editProduct.markupPercent != null ? String(editProduct.markupPercent) : '',
        stockQuantity: editProduct.stockQuantity != null ? String(editProduct.stockQuantity) : '',
        unit: editProduct.unit ?? 'pcs',
        trackStock: editProduct.trackStock ?? true,
        serialTracking: editProduct.serialTracking ?? false,
        expiryTracking: editProduct.expiryTracking ?? false,
        shelfLifeDays: editProduct.shelfLifeDays ? String(editProduct.shelfLifeDays) : '',
        barcode: editProduct.barcode ?? '',
        taxOverride: editProduct.taxOverride ?? false,
        taxRateIds: editProduct.taxes?.map((tx: ProductTaxLink) => tx.taxRateId || tx.taxRate?.id) ?? [],
        legacyTaxRate: Number(editProduct.taxRate ?? 0) || 0,
        clearLegacyTax: false,
        hsCode: editProduct.hsCode ?? '',
        uom: editProduct.uom ?? 'Pcs',
        skipPaymentMethodTax: editProduct.skipPaymentMethodTax ?? false,
        lowStockAlert: editProduct.lowStockAlert != null ? String(editProduct.lowStockAlert) : '5',
        reorderPoint: editProduct.reorderPoint != null ? String(editProduct.reorderPoint) : '',
        reorderQuantity: editProduct.reorderQuantity != null ? String(editProduct.reorderQuantity) : '',
        leadTimeDays: editProduct.leadTimeDays != null ? String(editProduct.leadTimeDays) : '',
        productType: editProduct.productType ?? 'REGULAR',
        imageUrl: editProduct.imageUrl ?? '',
      });
    } else if (dialogOpen) {
      setForm({ ...defaultForm, trackStock: canManageInventory });
      setInitialSerials('');
    }
    // Reset form tab and lookup result when dialog opens
    setFormTab('basics');
    setHsLookupResult(null);
  }, [dialogOpen, editProduct, canManageInventory]);

  // HS code search debounce
  useEffect(() => {
    if (hsSearchTerm.length < 2) { setHsResults([]); return; }
    const timer = setTimeout(() => {
      api.get(`/products/hs-search?q=${encodeURIComponent(hsSearchTerm)}`).then((r) => {
        setHsResults(r.data);
        setHsDropdownOpen(r.data.length > 0);
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [hsSearchTerm]);

  // Close HS dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (hsDropdownRef.current && !hsDropdownRef.current.contains(e.target as Node)) {
        setHsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => { const res = await api.get('/categories'); return res.data; },
  });

  const { data: brands } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => { const res = await api.get('/brands'); return res.data; },
  });

  // Serial numbers viewer query
  const canViewSerials = hasPermission('serial-numbers:read') || hasPermission('serial-numbers:*') || hasPermission('inventory:*');
  const { data: serialViewData, isLoading: serialViewLoading } = useQuery({
    queryKey: ['product-serials', serialViewProduct?.id, serialStatusFilter, serialSearch, serialPage],
    queryFn: () =>
      api.get(`/serial-numbers/by-product/${serialViewProduct!.id}`, {
        params: { status: serialStatusFilter || undefined, search: serialSearch || undefined, page: serialPage, limit: 50 },
      }).then((r) => r.data),
    enabled: !!serialViewProduct && canViewSerials,
    staleTime: 15_000,
  });

  const { data: taxRates } = useQuery({
    queryKey: ['tax-rates'],
    queryFn: async () => { const res = await api.get('/tax-rates'); return res.data; },
  });

  // Bundle queries/mutations
  // POS-I259: the bundle list searches the server (no fixed product cap). Any product except raw
  // materials can become a bundle by adding components; isBundle is computed server-side via _count.
  const [bundleSearchDebounced, setBundleSearchDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setBundleSearchDebounced(bundleSearch.trim()), 300);
    return () => clearTimeout(timer);
  }, [bundleSearch]);
  const { data: bundleData } = useQuery({
    queryKey: ['products', 'bundle-builder', bundleView, bundleSearchDebounced],
    queryFn: () => {
      const params: Record<string, string | number> = { limit: 60, page: 1, sortBy: 'name', sortOrder: 'asc', productType: 'REGULAR,SEMI_FINISHED' };
      if (bundleSearchDebounced) params.search = bundleSearchDebounced;
      if (bundleView === 'bundles') params.bundlesOnly = 'true';
      return api.get('/products', { params }).then(r => r.data);
    },
    placeholderData: (prev) => prev,
  });
  const { data: bundleCountData } = useQuery({
    queryKey: ['products', 'bundle-count'],
    queryFn: () => api.get('/products', { params: { limit: 1, bundlesOnly: 'true', productType: 'REGULAR,SEMI_FINISHED' } }).then(r => r.data),
  });
  const filteredBundleProducts: ProductRow[] = bundleData?.data ?? [];
  const bundleTotal: number = bundleData?.total ?? filteredBundleProducts.length;
  const bundleCount: number = bundleCountData?.total ?? 0;

  const { data: bundleComponents, refetch: refetchBundleComponents } = useQuery({
    queryKey: ['bundle-components', bundleProduct?.id],
    queryFn: () => api.get(`/products/${bundleProduct!.id}/bundles`).then(r => r.data),
    enabled: !!bundleProduct,
  });

  const addBundleComponentMutation = useMutation({
    mutationFn: ({ productId, componentId, quantity }: { productId: string; componentId: string; quantity: number }) =>
      api.post(`/products/${productId}/bundles`, { componentId, quantity }),
    onSuccess: () => {
      toast.success('Component added');
      setBundleComponentId('');
      setBundleQty('1');
      refetchBundleComponents();
    },
    onError: (err) => notify.error(err, 'Failed to add component'),
  });

  const removeBundleComponentMutation = useMutation({
    mutationFn: ({ productId, componentId }: { productId: string; componentId: string }) =>
      api.delete(`/products/${productId}/bundles/${componentId}`),
    onSuccess: () => {
      toast.success('Component removed');
      refetchBundleComponents();
    },
    onError: (err) => notify.error(err, 'Failed to remove component'),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: ProductSavePayload = {
        name: form.name,
        categoryId: form.categoryId || undefined,
        brandId: form.brandId || undefined,
        model: form.model || undefined,
        costPrice: form.costPrice ? Number(form.costPrice) : undefined,
        salePrice: Number(form.salePrice),
        cashPrice: form.cashPrice ? Number(form.cashPrice) : undefined,
        installmentPrice: form.installmentPrice ? Number(form.installmentPrice) : undefined,
        minimumPrice: form.minimumPrice ? Number(form.minimumPrice) : undefined,
        markupPercent: form.markupPercent ? Number(form.markupPercent) : undefined,
        // Only send stockQuantity on CREATE — stock is managed via inventory adjustments after that
        ...(editProduct ? {} : { stockQuantity: form.stockQuantity ? Number(form.stockQuantity) : undefined }),
        ...(!editProduct && openingNeedsConfirm && confirmOpening ? { confirmLargeValue: true } : {}),
        unit: form.unit || 'pcs',
        trackStock: form.trackStock,
        serialTracking: form.serialTracking,
        expiryTracking: form.expiryTracking,
        shelfLifeDays: form.expiryTracking && form.shelfLifeDays ? Number(form.shelfLifeDays) : undefined,
        barcode: form.barcode || undefined,
        taxOverride: form.taxOverride,
        ...(form.taxOverride && { taxRateIds: form.taxRateIds }),
        ...(form.clearLegacyTax && { taxRate: 0 }),
        hsCode: form.hsCode || undefined,
        uom: form.uom || 'Pcs',
        skipPaymentMethodTax: form.skipPaymentMethodTax,
        lowStockAlert: form.lowStockAlert ? Number(form.lowStockAlert) : undefined,
        reorderPoint: form.reorderPoint ? Number(form.reorderPoint) : undefined,
        reorderQuantity: form.reorderQuantity ? Number(form.reorderQuantity) : undefined,
        leadTimeDays: form.leadTimeDays ? Number(form.leadTimeDays) : undefined,
        productType: form.productType,
        imageUrl: form.imageUrl || undefined,
      };
      if (editProduct) {
        const { data } = await api.patch(`/products/${editProduct.id}`, payload);
        return data;
      }
      payload.sku = form.sku;
      // Attach initial serial numbers for serial-tracked products
      if (form.serialTracking && initialSerials.trim()) {
        const parsed = initialSerials.split('\n').map((s) => s.trim()).filter(Boolean);
        const qty = form.stockQuantity ? Number(form.stockQuantity) : 0;
        if (parsed.length !== qty) {
          throw new Error(`Serial count (${parsed.length}) must match stock quantity (${qty}).`);
        }
        payload.serialNumbers = parsed;
      }
      const { data } = await api.post('/products', payload);
      return data;
    },
    onSuccess: () => {
      toast.success(editProduct ? 'Product updated' : 'Product created');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['products-pos'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      setDialogOpen(false);
    },
    onError: (error: unknown) => {
      // Precedence preserved: API message -> raw error message (e.g. the local serial-count Error) -> 'Save failed'.
      toast.error(getApiErrorMessage(error, '') || 'Save failed');
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['products', search, page, stockFilter, filterCategoryId],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      if (stockFilter === 'low-stock') params.set('lowStockOnly', 'true');
      if (stockFilter === 'out-of-stock') params.set('outOfStock', 'true');
      if (filterCategoryId) params.set('categoryId', filterCategoryId);
      const { data } = await api.get(`/products?${params}`);
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/products/${id}`),
    onSuccess: () => {
      toast.success('Product deleted');
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err) => notify.error(err, 'Delete failed'),
  });

  const bulkCategoryMutation = useMutation({
    mutationFn: async ({ ids, categoryId }: { ids: string[]; categoryId: string }) => {
      await Promise.all(ids.map((id) => api.patch(`/products/${id}`, { categoryId })));
    },
    onSuccess: () => {
      toast.success(`Updated ${selectedIds.size} product category(s)`);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setSelectedIds(new Set());
      setBulkCategoryId('');
    },
    onError: () => toast.error('Bulk category update failed'),
  });

  const bulkPriceAdjustMutation = useMutation({
    mutationFn: (updates: Array<{ id: string; salePrice: number }>) =>
      api.post('/products/bulk-price-adjust', { updates }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Updated ${data.updated} product price(s)`);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      setBulkPriceModalOpen(false);
      setSelectedIds(new Set());
    },
    onError: (err) => notify.error(err, 'Price update failed'),
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (productIds: string[]) => {
    if (productIds.every((id) => selectedIds.has(id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(productIds));
    }
  };

  async function exportCSV() {
    setExportLoading(true);
    try {
      const res = await api.get('/products/export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  }

  return (
    <div className="page-enter">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        action={
          <div className="flex gap-2">
            {canExport && (
              <Button
                variant="outline"
                onClick={exportCSV}
                disabled={exportLoading}
              >
                {exportLoading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Download className="me-2 h-4 w-4" />}
                {t('exportCsv')}
              </Button>
            )}
            {canCreate && (
              <Button
                variant="gradient"
                onClick={() => { setEditProduct(null); setDialogOpen(true); }}
              >
                <Plus className="me-2 h-4 w-4" />
                {t('addProduct')}
              </Button>
            )}
          </div>
        }
      />

      {isTestMode && (
        <AlertBanner tone="warning" icon={FlaskConical}>
          <strong>Test Mode:</strong> You are viewing test catalog data. Switch to Production in <a href="/settings" className="underline">Settings</a> to manage live products.
        </AlertBanner>
      )}

      <TabBar
        className="mb-4"
        tabs={[
          { key: 'products', label: t('title'), icon: Package },
          ...(canManageInventory ? [{ key: 'bundles', label: t('productBundles'), icon: Layers }] : []),
        ]}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as 'products' | 'bundles')}
      />

      {/* ── Bundles tab ── */}
      {activeTab === 'bundles' && (
        <div className="space-y-4">
          <Card className="animate-fade-in-up">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-4">
                {t('bundleDesc')}
              </p>
              {bundleData && filteredBundleProducts.length === 0 && !bundleSearchDebounced && bundleView === 'all' ? (
                <EmptyState
                  icon={Layers}
                  title={t('noBundleProducts')}
                  description={t('noBundleProductsDesc')}
                  action={canCreate ? { label: t('addProduct'), onClick: () => { setEditProduct(null); setDialogOpen(true); } } : undefined}
                />
              ) : (
                <>
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <SearchInput value={bundleSearch} onChange={setBundleSearch} placeholder="Search products by name or SKU…" />
                  </div>
                  <div className="inline-flex shrink-0 rounded-lg border p-0.5 text-xs font-medium">
                    <button type="button" onClick={() => setBundleView('all')} className={`rounded-md px-3 py-1 transition-colors ${bundleView === 'all' ? 'bg-primary/10 text-primary-text' : 'text-muted-foreground hover:text-foreground'}`}>{t('allProducts', { defaultValue: 'All products' })}</button>
                    <button type="button" onClick={() => setBundleView('bundles')} className={`rounded-md px-3 py-1 transition-colors ${bundleView === 'bundles' ? 'bg-primary/10 text-primary-text' : 'text-muted-foreground hover:text-foreground'}`}>{t('productBundles')}{bundleCount ? ` (${bundleCount})` : ''}</button>
                  </div>
                </div>
                {filteredBundleProducts.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">{bundleView === 'bundles' ? t('noBundlesYetHint', { defaultValue: 'No bundles yet — switch to “All products” and add components to a product to create one.' }) : t('noMatches', { defaultValue: 'No products match your search.' })}</p>
                ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredBundleProducts.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setBundleProduct(p)}
                      className={`flex items-center gap-3 rounded-xl border p-4 text-start transition-all hover:shadow-md hover:border-primary/30 ${bundleProduct?.id === p.id ? 'border-primary bg-primary/10' : 'bg-card'}`}
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                        <Layers className="h-5 w-5 text-primary-text" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{p.name}</p>
                          {p.isBundle && (
                            <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary-text">
                              Bundle
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{p.sku ?? 'No SKU'} · {formatCurrency(Number(p.salePrice))}</p>
                      </div>
                    </button>
                  ))}
                </div>
                )}
                {bundleTotal > filteredBundleProducts.length && (
                  <p className="mt-3 text-center text-xs text-muted-foreground">{t('bundleListMore', { defaultValue: 'Showing the first {{shown}} of {{total}} products. Search to narrow the list.', shown: filteredBundleProducts.length, total: bundleTotal })}</p>
                )}
                </>
              )}
            </CardContent>
          </Card>

          <Dialog
            visible={!!bundleProduct}
            onHide={() => setBundleProduct(null)}
            header={bundleProduct ? `${bundleProduct.name} — ${t('components', { defaultValue: 'Components' })}` : ''}
            style={{ width: '44rem' }}
            contentStyle={{ maxHeight: '75vh', overflowY: 'auto' }}
            draggable={false}
            resizable={false}
          >
            {bundleProduct && (
              <div className="space-y-4">
                {/* Add component */}
                {canUpdate && (
                  <div className="flex gap-2 items-end flex-wrap">
                    <div className="flex-1 min-w-48">
                      <Label className="text-xs text-muted-foreground mb-1 block">{t('componentProduct')}</Label>
                      <ProductSearchCombobox
                        value={bundleComponentId}
                        onChange={(id) => setBundleComponentId(id)}
                        excludeId={bundleProduct.id}
                        placeholder="Select component..."
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Qty</Label>
                      <Input type="number" min={1} step={1} value={bundleQty} onChange={e => setBundleQty(e.target.value)}
                        className="w-20 h-9" />
                    </div>
                    <Button
                      onClick={() => {
                        if (!bundleComponentId) { toast.error('Select a component product'); return; }
                        const qty = Number(bundleQty);
                        if (!qty || qty < 1) { toast.error('Quantity must be at least 1'); return; }
                        addBundleComponentMutation.mutate({ productId: bundleProduct.id, componentId: bundleComponentId, quantity: qty });
                      }}
                      disabled={addBundleComponentMutation.isPending}
                    >
                      {addBundleComponentMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      Add
                    </Button>
                  </div>
                )}

                {/* Components list */}
                {!bundleComponents || bundleComponents.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">{t('noComponents')}</p>
                ) : (
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                          <th className="px-4 py-2 text-start font-medium">{t('component')}</th>
                          <th className="px-4 py-2 text-end font-medium">Qty</th>
                          <th className="px-4 py-2 text-end font-medium">Unit Price</th>
                          {canUpdate && <th className="px-4 py-2 text-center font-medium">{t('remove')}</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {bundleComponents.map((comp: BundleComponentRow) => (
                          <tr key={comp.componentId || comp.id} className="hover:bg-muted/20">
                            <td className="px-4 py-2.5 font-medium">{comp.component?.name ?? comp.name}</td>
                            <td className="px-4 py-2.5 text-end">{comp.quantity}</td>
                            <td className="px-4 py-2.5 text-end font-mono text-muted-foreground">{formatCurrency(Number(comp.component?.salePrice ?? comp.salePrice ?? 0))}</td>
                            {canUpdate && (
                              <td className="px-4 py-2.5 text-center">
                                <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-destructive-text hover:text-destructive-text hover:bg-destructive/10 border-destructive/30"
                                  onClick={() => removeBundleComponentMutation.mutate({ productId: bundleProduct.id, componentId: comp.componentId ?? comp.id })}
                                  disabled={removeBundleComponentMutation.isPending}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </Dialog>
        </div>
      )}

      {/* ── Products tab ── */}
      {activeTab === 'products' && (<>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onChange={(value) => { setSearch(value); setPage(1); }}
            placeholder={t('searchPlaceholder')}
            className="max-w-sm w-full"
          />

          {/* Quick stock-filter chips */}
          <div className="flex items-center gap-1.5">
            {(['all', 'low-stock', 'out-of-stock'] as const).map((f) => {
              const labels = { all: t('allStock'), 'low-stock': t('lowStockFilter'), 'out-of-stock': t('outOfStockFilter') };
              const active = stockFilter === f;
              return (
                <button
                  key={f}
                  onClick={() => { setStockFilter(f); setPage(1); }}
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border transition-all ${
                    active
                      ? f === 'all'
                        ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                        : f === 'low-stock'
                          ? 'bg-warning text-warning-foreground border-warning shadow-sm'
                          : 'bg-destructive text-destructive-foreground border-destructive shadow-sm'
                      : 'bg-background text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground'
                  }`}
                >
                  {labels[f]}
                </button>
              );
            })}
          </div>

          {/* Category filter */}
          {categories && categories.length > 0 && (
            <Dropdown
              value={filterCategoryId}
              onChange={(e) => { setFilterCategoryId(e.value); setPage(1); }}
              options={[{ label: t('allCategories'), value: '' }, ...categories.map((cat: CategoryRow) => ({ label: cat.name, value: cat.id }))]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('allCategories')}
            />
          )}

          {/* Clear filters */}
          {(stockFilter !== 'all' || filterCategoryId) && (
            <button
              onClick={() => { setStockFilter('all'); setFilterCategoryId(''); setPage(1); }}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              {t('clearFilters')}
            </button>
          )}
        </div>

      {/* Bulk action bar */}
      {canUpdate && selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-2.5">
          <span className="text-sm font-semibold text-primary-text">{selectedIds.size} selected</span>
          <div className="flex gap-2 ms-auto">
            <div className="flex items-center gap-1.5">
              <Dropdown
                value={bulkCategoryId}
                onChange={(e) => setBulkCategoryId(e.value)}
                options={[{ label: '— Move to category —', value: '' }, ...(categories ?? []).map((cat: CategoryRow) => ({ label: cat.name, value: cat.id }))]}
                optionLabel="label"
                optionValue="value"
                placeholder="— Move to category —"
              />
              {bulkCategoryId && (
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => bulkCategoryMutation.mutate({ ids: [...selectedIds], categoryId: bulkCategoryId })} disabled={bulkCategoryMutation.isPending}>
                  Apply
                </Button>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs gap-1"
              onClick={() => {
                const selectedProducts = (data?.data ?? []).filter((p: ProductRow) => selectedIds.has(p.id));
                setBulkPriceRows(selectedProducts.map((p: ProductRow) => ({
                  id: p.id,
                  name: p.name,
                  sku: p.sku ?? '',
                  costPrice: Number(p.costPrice ?? 0),
                  salePrice: Number(p.salePrice ?? 0),
                  newSalePrice: String(Number(p.salePrice ?? 0)),
                })));
                setBulkPriceModalOpen(true);
              }}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Adjust Prices
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setSelectedIds(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}
      {/* Product table */}
      <Card className="animate-fade-in-up">
        <CardContent className="p-0">
          {isLoading ? (
            <SkeletonTable rows={8} cols={10} className="p-4" />
          ) : !data?.data?.length ? (
            <EmptyState
              icon={Package}
              title={t('noProductsFound')}
              description={t('addFirstProductDesc')}
              action={canCreate ? { label: t('addProduct'), onClick: () => { setEditProduct(null); setDialogOpen(true); } } : undefined}
            />
          ) : (
          <>
            {/* Multi-select hint — shown when nothing is selected yet */}
            {canUpdate && selectedIds.size === 0 && (
              <div className="flex items-center gap-1.5 px-4 py-2 bg-muted/30 border-b text-xs text-muted-foreground">
                <SlidersHorizontal className="h-3 w-3 shrink-0" />
                <span>Tick checkboxes to select products, then use the action bar to <strong>adjust prices</strong> or <strong>move categories</strong> in bulk.</span>
              </div>
            )}
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  {canUpdate && <Th className="px-3"><Checkbox className="h-3.5 w-3.5" checked={data?.data?.length > 0 && data.data.every((p: ProductRow) => selectedIds.has(p.id))} onChange={() => toggleSelectAll(data?.data?.map((p: ProductRow) => p.id) ?? [])} /></Th>}
                  <Th>{t('productColumn')}</Th>
                  <Th>{t('skuColumn')}</Th>
                  <Th>{t('category')}</Th>
                  <Th className="text-end">{t('costColumn')}</Th>
                  <Th className="text-end">{t('priceColumn')}</Th>
                  <Th className="text-center">{t('stockColumn')}</Th>
                  <Th className="text-center">{t('typeColumn')}</Th>
                  {canFbrLookup && <Th className="text-center">{t('uom')}</Th>}
                  {canFbrLookup && <Th className="text-center">{t('fbrColumn')}</Th>}
                  <Th className="text-end">{t('actionsColumn')}</Th>
                </Tr>
              </THead>
              <TBody>
                {isLoading ? null : data?.data?.map((product: ProductRow) => (
                    <Tr key={product.id} className={selectedIds.has(product.id) ? 'bg-primary/5' : ''}>
                      {canUpdate && <Td className="px-3"><Checkbox className="h-3.5 w-3.5" checked={selectedIds.has(product.id)} onChange={() => toggleSelect(product.id)} /></Td>}
                      <Td label={t('productColumn')} className="font-medium">
                        <div className="flex items-center gap-3">
                          {product.imageUrl ? (
                            <img src={product.imageUrl} alt={product.name} className="h-8 w-8 rounded object-cover flex-shrink-0 border border-border" />
                          ) : (
                            <div className="h-8 w-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
                              {product.trackStock === false
                                ? <ChefHat className="h-4 w-4 text-muted-foreground/50" />
                                : <Package className="h-4 w-4 text-muted-foreground/50" />}
                            </div>
                          )}
                          <div className="min-w-0">
                            <span className="block truncate">{product.name}</span>
                            {/* Tax badges */}
                            {product.taxes?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {product.taxes.slice(0, 2).map((txBadge: ProductTaxLink) => (
                                  <span key={txBadge.taxRateId || txBadge.id} className="inline-flex items-center rounded px-1 py-px text-[10px] font-medium bg-info/10 text-info-text border border-info/30">
                                    <Receipt className="h-2 w-2 me-0.5" />{txBadge.taxRate?.name ?? txBadge.name}
                                  </span>
                                ))}
                                {product.taxes.length > 2 && <span className="text-[10px] text-muted-foreground">+{product.taxes.length - 2}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      </Td>
                      <Td label={t('skuColumn')} className="text-muted-foreground">
                        <span className="font-mono text-xs">{product.sku}</span>
                        {product.barcode && (
                          <span className="ms-2 inline-flex items-center gap-0.5 text-xs text-muted-foreground/50">
                            <ScanBarcode className="h-3 w-3" />
                          </span>
                        )}
                      </Td>
                      <Td label={t('category')} className="text-muted-foreground">
                        {product.category?.name || '—'}
                      </Td>
                      <Td label={t('costColumn')} className="text-end">
                        {formatCurrency(Number(product.costPrice))}
                      </Td>
                      <Td label={t('priceColumn')} className="text-end font-medium">
                        {formatCurrency(Number(product.salePrice))}
                      </Td>
                      <Td label={t('stockColumn')} className="text-center">
                        {product.trackStock === false ? (
                          <span className="text-muted-foreground/40 text-xs">—</span>
                        ) : product.stockQuantity <= 0 ? (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-destructive/10 text-destructive-text border border-destructive/30">
                            {product.stockQuantity}
                          </span>
                        ) : product.lowStockAlert != null && product.stockQuantity <= product.lowStockAlert ? (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-warning/10 text-warning-text border border-warning/30">
                            {product.stockQuantity}
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-success/10 text-success-text border border-success/30">
                            {product.stockQuantity}
                          </span>
                        )}
                      </Td>
                      <Td label={t('typeColumn')} className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          {product.trackStock === false ? (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-warning/10 text-warning-text border border-warning/30">
                              <ChefHat className="h-3 w-3" />Prepared
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-success/10 text-success-text border border-success/30">
                              <Warehouse className="h-3 w-3" />Tracked
                            </span>
                          )}
                          {product.serialTracking && (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary-text border border-primary/30" title="Serial / IMEI tracked">
                              <Hash className="h-3 w-3" />Serial
                            </span>
                          )}
                          {(hasPermission('tax-rates:*') || hasPermission('restaurant:*')) && product.skipPaymentMethodTax && (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-info/10 text-info-text border border-info/30" title="Skips ICT service tax — product has its own embedded GST">
                              <ShieldOff className="h-3 w-3" />Own GST
                            </span>
                          )}
                        </div>
                      </Td>
                      {canFbrLookup && (
                      <Td label={t('uom')} className="text-center">
                        {product.uom ? (
                          <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-mono font-medium bg-muted text-muted-foreground ring-1 ring-inset ring-border">
                            {product.uom}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/40 text-xs">—</span>
                        )}
                      </Td>
                      )}
                      {canFbrLookup && (
                      <Td label={t('fbrColumn')} className="text-center text-xs">
                        {product.hsCode ? (
                          <span className="inline-flex items-center rounded px-1.5 py-0.5 font-mono text-xs bg-success/10 text-success-text border border-success/30">
                            {product.hsCode}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/40 text-xs">—</span>
                        )}
                      </Td>
                      )}
                      <Td label={t('actionsColumn')} className="text-end">
                        <TableRowActions
                          actions={[
                            {
                              key: 'serials',
                              label: t('viewSerialNumbers'),
                              icon: List,
                              onSelect: () => { setSerialViewProduct(product); setSerialStatusFilter(''); setSerialSearch(''); setSerialPage(1); },
                              permission: !!(product.serialTracking && canViewSerials),
                              primary: true,
                            },
                            {
                              key: 'edit',
                              label: t('editProduct'),
                              icon: Edit,
                              onSelect: () => { setEditProduct(product); setDialogOpen(true); },
                              permission: canUpdate,
                              primary: true,
                            },
                            {
                              key: 'delete',
                              label: t('deleteProduct'),
                              icon: Trash2,
                              onSelect: () => setDeleteConfirm({ open: true, id: product.id, name: product.name }),
                              permission: canDelete,
                              variant: 'destructive',
                            },
                          ]}
                        />
                      </Td>
                    </Tr>
                  ))
                }
              </TBody>
            </Table>
            </div>

            {/* Mobile card list */}
            <div className="sm:hidden space-y-3 p-3">
              {data?.data?.map((product: ProductRow) => (
                <div key={product.id} className="rounded-lg border border-border bg-card p-3 shadow-sm">
                  <div className="flex items-start gap-3">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt={product.name} className="h-12 w-12 rounded object-cover flex-shrink-0 border border-border" />
                    ) : (
                      <div className="h-12 w-12 rounded bg-muted flex items-center justify-center flex-shrink-0">
                        {product.trackStock === false
                          ? <ChefHat className="h-5 w-5 text-muted-foreground/50" />
                          : <Package className="h-5 w-5 text-muted-foreground/50" />}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.sku || '—'}</p>
                      <p className="text-xs text-muted-foreground">{product.category?.name || '—'}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {product.trackStock === false ? (
                          <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-warning/10 text-warning-text border border-warning/30">
                            <ChefHat className="h-2.5 w-2.5" />Prepared
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-success/10 text-success-text border border-success/30">
                            <Warehouse className="h-2.5 w-2.5" />Tracked
                          </span>
                        )}
                        {canFbrLookup && product.uom && (
                          <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-mono font-medium bg-muted text-muted-foreground ring-1 ring-inset ring-border">
                            {product.uom}
                          </span>
                        )}
                        {canFbrLookup && product.hsCode && (
                          <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-mono bg-success/10 text-success-text border border-success/30">
                            {product.hsCode}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-end shrink-0 space-y-0.5">
                      <p className="font-bold text-sm">{formatCurrency(Number(product.salePrice))}</p>
                      <p className="text-xs text-muted-foreground">Cost: {formatCurrency(Number(product.costPrice))}</p>
                      {product.trackStock !== false && (
                        <p className={`text-xs font-medium ${product.lowStockAlert != null && product.stockQuantity <= product.lowStockAlert ? 'text-destructive-text' : 'text-muted-foreground'}`}>
                          Stock: {product.stockQuantity}
                        </p>
                      )}
                    </div>
                  </div>
                  {(canUpdate || canDelete) && (
                    <div className="flex gap-2 mt-3 pt-2 border-t border-border">
                      {canUpdate && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 h-9"
                          onClick={() => { setEditProduct(product); setDialogOpen(true); }}
                        >
                          <Edit className="h-3.5 w-3.5 me-1.5" />Edit
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 h-9 text-destructive-text hover:text-destructive-text"
                          onClick={() => setDeleteConfirm({ open: true, id: product.id, name: product.name })}
                        >
                          <Trash2 className="h-3.5 w-3.5 me-1.5" />Delete
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
          )} {/* end else */}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data?.meta && (
        <PaginationBar
          page={page}
          pageSize={20}
          total={data.meta.total}
          onPageChange={setPage}
        />
      )}
      {/* Add/Edit Product Dialog */}
      <Dialog
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        header={editProduct ? t('editProduct') : t('addProduct')}
        style={{ width: '60rem' }}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        maximizable
        draggable={false}
        resizable={false}
        footer={
          <div className="flex gap-3">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              form="product-form"
              variant="gradient"
              className="flex-1"
              disabled={saveMutation.isPending || !form.name || !form.salePrice || (!editProduct && !form.sku) || (openingNeedsConfirm && !confirmOpening)}
            >
              {saveMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {editProduct ? t('updateProduct') : t('createProduct')}
            </Button>
          </div>
        }
      >
        <form id="product-form" onSubmit={(e) => {
          e.preventDefault();
          if (form.hsCode && !/^\d{4}\.\d{4}$/.test(form.hsCode)) {
            toast.error('HS Code must be in format XXXX.XXXX (e.g. 1234.5678)');
            return;
          }
          saveMutation.mutate();
        }}>
        <div className="space-y-0">
          {/* Tab bar */}
          <div className="flex border-b border-border bg-muted/30 -mx-[var(--dialog-padding,1.5rem)] px-6 mb-4">
            {([
              { id: 'basics' as const, label: '📦 Basics' },
              { id: 'pricing' as const, label: '💰 Pricing & Tax' },
              { id: 'stock' as const, label: '🏷️ Stock' },
              ...(canFbrLookup ? [{ id: 'fbr' as const, label: '📋 FBR' }] : []),
            ]).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setFormTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  formTab === tab.id
                    ? 'border-primary text-primary-text'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── TAB 1: BASICS ── */}
          {formTab === 'basics' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('nameRequired')}</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Product name"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>{t('skuRequired')}</Label>
                  <Input
                    dir="ltr"
                    value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                    placeholder="e.g. BEV-001"
                    disabled={!!editProduct}
                    className={editProduct ? 'opacity-60' : ''}
                  />
                  {editProduct && <p className="mt-1 text-xs text-muted-foreground">{t('skuCannotChange')}</p>}
                </div>
              </div>

              <div>
                <Label>Category</Label>
                <Combobox
                  options={(Array.isArray(categories) ? categories : categories?.data ?? []).map((cat: CategoryRow) => ({ value: cat.id, label: cat.name }))}
                  value={form.categoryId}
                  onChange={(val) => setForm({ ...form, categoryId: val })}
                  placeholder="— None —"
                  searchPlaceholder="Search categories..."
                  emptyText="No categories found."
                  allowClear
                />
                {/* Show inherited category taxes */}
                {(() => {
                  const catList = Array.isArray(categories) ? categories : categories?.data ?? [];
                  const selectedCat = catList.find((c: CategoryRow) => c.id === form.categoryId);
                  const catTaxes = selectedCat?.taxes ?? [];
                  if (!form.categoryId || catTaxes.length === 0) return null;
                  return (
                    <div className="mt-2 rounded-lg border border-primary/15 bg-primary/5 px-3 py-2">
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        Taxes from "{selectedCat.name}":
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {catTaxes.map((ct: CategoryTaxLink) => (
                          <span key={ct.taxRate?.id || ct.taxRateId} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary-text">
                            {ct.taxRate?.name?.includes('%') ? ct.taxRate.name : `${ct.taxRate?.name} — ${ct.taxRate?.rate}%`}
                            <span className="text-[10px] opacity-70">({ct.taxRate?.type === 'INCLUSIVE' ? 'incl' : 'excl'})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div>
                <Label>Brand</Label>
                <Combobox
                  options={(Array.isArray(brands) ? brands : brands?.data ?? []).map((b: BrandRow) => ({ value: b.id, label: b.name }))}
                  value={form.brandId}
                  onChange={(val) => setForm({ ...form, brandId: val })}
                  placeholder="— None —"
                  searchPlaceholder="Search brands..."
                  emptyText="No brands found."
                  allowClear
                />
              </div>

              <div>
                <Label>Model</Label>
                <Input
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  placeholder="Manufacturer model / part number"
                />
              </div>

              <div>
                <Label>{t('barcode')}</Label>
                <Input
                  dir="ltr"
                  value={form.barcode}
                  onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                  placeholder="Scan or type barcode"
                />
                {form.barcode && (
                  <div className="mt-3 flex flex-col items-center rounded-xl border border-border/50 bg-background py-3">
                    <BarcodeDisplay value={form.barcode} height={56} />
                    <p className="mt-1 text-xs text-muted-foreground">CODE128 — ready to scan</p>
                  </div>
                )}
              </div>

              <div>
                <Label>Product Image URL</Label>
                <Input
                  value={form.imageUrl}
                  onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                  placeholder="https://example.com/product.jpg"
                  type="url"
                />
                {form.imageUrl && (
                  <div className="mt-2 flex items-center gap-3">
                    <img src={form.imageUrl} alt="Product preview" className="h-16 w-16 rounded-lg object-cover border border-border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    <p className="text-xs text-muted-foreground">Preview (loads from the URL above)</p>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3 rounded-lg border border-border/60 bg-muted/20 p-4">
                <Label className={`flex items-center gap-3 ${canManageInventory ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                  <Checkbox
                    id="trackStock"
                    checked={form.trackStock}
                    disabled={!canManageInventory}
                    onChange={(e) => {
                      const newVal = e.target.checked;
                      if (!newVal && editProduct && editProduct.stockQuantity > 0) {
                        // T-112: replace window.confirm with ConfirmDialog (UB-005)
                        setDisableTrackStockConfirm({ open: true, stockQuantity: editProduct.stockQuantity });
                        return;
                      }
                      setForm({ ...form, trackStock: newVal });
                    }}
                  />
                  <div>
                    <span className="text-sm font-medium">{t('trackStock')}</span>
                    <p className="text-xs text-muted-foreground">
                      {canManageInventory
                        ? 'Enable inventory quantity tracking'
                        : 'Requires the Inventory & Purchases addon to enable stock tracking'}
                    </p>
                  </div>
                </Label>
                <Label className={`flex items-center gap-3 ${editProduct ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                  <Checkbox
                    id="serialTracking"
                    checked={form.serialTracking}
                    onChange={(e) => setForm({ ...form, serialTracking: e.target.checked })}
                    disabled={!!editProduct}
                  />
                  <div>
                    <span className="text-sm font-medium">{t('serialTrackingLabel')}</span>
                    <p className="text-xs text-muted-foreground">
                      {editProduct ? 'Cannot change after creation' : t('serialTrackingDesc')}
                    </p>
                  </div>
                </Label>
                <Label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    id="expiryTracking"
                    checked={form.expiryTracking}
                    onChange={(e) => setForm({ ...form, expiryTracking: e.target.checked })}
                  />
                  <div>
                    <span className="text-sm font-medium">{t('expiryTrackingLabel')}</span>
                    <p className="text-xs text-muted-foreground">{t('expiryTrackingDesc')}</p>
                  </div>
                </Label>
                {form.expiryTracking && (
                  <div className="ps-8">
                    <Label htmlFor="shelfLifeDays" className="text-xs">{t('shelfLifeDaysLabel')}</Label>
                    <Input
                      id="shelfLifeDays"
                      type="number"
                      min={1}
                      step={1}
                      value={form.shelfLifeDays}
                      onChange={(e) => setForm({ ...form, shelfLifeDays: e.target.value })}
                      className="h-11 w-40 text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Product Type */}
              <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
                <p className="text-sm font-semibold mb-3">{t('productType')}</p>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { value: 'REGULAR',       label: 'Regular',       desc: 'Standard sellable product' },
                    { value: 'RAW_MATERIAL',  label: 'Raw Material',  desc: 'Used as ingredient in recipes' },
                    { value: 'SEMI_FINISHED', label: 'Semi-Finished', desc: 'Intermediate production output' },
                  ] as const).map(opt => (
                    <label
                      key={opt.value}
                      className={`flex flex-col gap-1 rounded-lg border p-3 cursor-pointer transition-colors ${
                        form.productType === opt.value
                          ? 'border-primary bg-primary/5 text-primary-text'
                          : 'border-border hover:bg-muted/40'
                      }`}
                    >
                      <input
                        type="radio" name="productType" value={opt.value}
                        checked={form.productType === opt.value}
                        onChange={() => setForm({ ...form, productType: opt.value })}
                        className="sr-only"
                      />
                      <span className="text-xs font-semibold">{opt.label}</span>
                      <span className={`text-xs ${form.productType === opt.value ? 'text-primary-text' : 'text-muted-foreground'}`}>{opt.desc}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── TAB 2: PRICING & TAX ── */}
          {formTab === 'pricing' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('costPrice')}{editProduct && <span className="ms-1 text-xs text-muted-foreground">(managed by avg. cost)</span>}</Label>
                  <Input
                    type="number"
                    value={form.costPrice}
                    onChange={(e) => !editProduct && setForm({ ...form, costPrice: e.target.value })}
                    placeholder="0.00"
                    min={0}
                    step={0.01}
                    readOnly={!!editProduct}
                    disabled={!!editProduct}
                    className={editProduct ? 'bg-muted cursor-not-allowed opacity-70' : ''}
                    title={editProduct ? 'Cost price is managed automatically via average cost calculations' : undefined}
                  />
                </div>
                <div>
                  <Label>{t('salePriceRequired')}</Label>
                  <Input
                    type="number"
                    value={form.salePrice}
                    onChange={(e) => setForm({ ...form, salePrice: e.target.value })}
                    placeholder="0.00"
                    min={0}
                    step={0.01}
                  />
                  {/* Dynamic inclusive/exclusive hint */}
                  {(() => {
                    const activeTaxRateList: TaxRateRow[] = Array.isArray(taxRates) ? taxRates : [];
                    let effectiveTypes: string[] = [];
                    if (form.taxOverride && form.taxRateIds.length > 0) {
                      effectiveTypes = activeTaxRateList
                        .filter((tx) => form.taxRateIds.includes(tx.id))
                        .map((tx) => tx.type);
                    }
                    const hasInclusive = effectiveTypes.includes('INCLUSIVE');
                    const hasExclusive = effectiveTypes.includes('EXCLUSIVE');
                    if (hasInclusive && !hasExclusive) return <p className="mt-1 text-xs text-info-text">Enter price <strong>with tax already included</strong> — tax extracted at checkout.</p>;
                    if (hasExclusive && !hasInclusive) return <p className="mt-1 text-xs text-muted-foreground">Enter price <strong>before tax</strong> — tax added on top at checkout.</p>;
                    if (hasInclusive && hasExclusive) return <p className="mt-1 text-xs text-warning-text">Mixed types — some rates inclusive (in price), some exclusive (added on top).</p>;
                    return null;
                  })()}
                </div>
              </div>

              {/* MEIMS multi-tier pricing & markup */}
              <div className="border border-border/60 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-muted/30">
                  <span className="text-sm font-medium">Multi-tier Pricing & Markup</span>
                  <p className="text-xs text-muted-foreground mt-0.5">Optional MEIMS pricing tiers. Setting markup % auto-fills cash price from cost on save.</p>
                </div>
                <div className="grid grid-cols-2 gap-4 px-4 py-3">
                  <div>
                    <Label>Markup %</Label>
                    <Input
                      type="number"
                      value={form.markupPercent}
                      onChange={(e) => setForm({ ...form, markupPercent: e.target.value })}
                      placeholder="e.g. 25"
                      min={0}
                      step={0.01}
                    />
                    {form.costPrice && form.markupPercent && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Auto cash price: {formatCurrency(Math.round(Number(form.costPrice) * (1 + Number(form.markupPercent) / 100) * 100) / 100)}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Cash Price</Label>
                    <Input
                      type="number"
                      value={form.cashPrice}
                      onChange={(e) => setForm({ ...form, cashPrice: e.target.value })}
                      placeholder="Auto from markup if blank"
                      min={0}
                      step={0.01}
                    />
                  </div>
                  <div>
                    <Label>Installment Price</Label>
                    <Input
                      type="number"
                      value={form.installmentPrice}
                      onChange={(e) => setForm({ ...form, installmentPrice: e.target.value })}
                      placeholder="0.00"
                      min={0}
                      step={0.01}
                    />
                  </div>
                  <div>
                    <Label>Minimum Price</Label>
                    <Input
                      type="number"
                      value={form.minimumPrice}
                      onChange={(e) => setForm({ ...form, minimumPrice: e.target.value })}
                      placeholder="Floor price"
                      min={0}
                      step={0.01}
                    />
                  </div>
                </div>
              </div>

              <div className="border border-border/60 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
                  <span className="text-sm font-medium">{t('taxConfiguration')}</span>
                  {form.categoryId && (
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-muted-foreground">Override category taxes</span>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, taxOverride: !form.taxOverride })}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                          form.taxOverride ? 'bg-gradient-to-r from-primary to-primary' : 'bg-muted'
                        }`}
                      >
                        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-background shadow-lg transition duration-200 ${form.taxOverride ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </Label>
                  )}
                  {!form.categoryId && <span className="text-xs text-warning-text">Select a category to inherit taxes</span>}
                </div>
                <div className="px-4 py-3">
                  {!form.taxOverride && form.categoryId && (
                    <p className="text-xs text-success-text flex items-center gap-1">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-success" />
                      Using taxes inherited from category. Toggle override to customize.
                    </p>
                  )}
                  {!form.taxOverride && !form.categoryId && (
                    <p className="text-xs text-muted-foreground">Assign a category on the Basics tab to inherit its taxes, or enable override for custom taxes.</p>
                  )}
                  {form.taxOverride && (
                    <div className="space-y-1 rounded-lg border border-border/50 bg-accent/30 p-3">
                      <p className="text-xs text-warning-text flex items-center gap-1 mb-2">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-warning" />
                        Custom taxes (category taxes ignored)
                      </p>
                      {(Array.isArray(taxRates) ? taxRates : []).filter((tx: TaxRateRow) => tx.isActive !== false).map((tax: TaxRateRow) => (
                        <Label key={tax.id} className="flex items-center gap-2 cursor-pointer hover:bg-accent/50 rounded px-2 py-1.5 transition-colors">
                          <Checkbox
                            checked={form.taxRateIds.includes(tax.id)}
                            onChange={(e) => setForm({
                              ...form,
                              taxRateIds: e.target.checked
                                ? [...form.taxRateIds, tax.id]
                                : form.taxRateIds.filter((id: string) => id !== tax.id),
                            })}
                          />
                          <span className="text-sm">{tax.name}</span>
                          <span className="ms-auto text-xs font-medium">
                            {tax.rate}%
                            {tax.type === 'INCLUSIVE'
                              ? <span className="ms-1 text-info-text" title="Price already includes this tax — extracted at checkout."> · Inclusive (in price)</span>
                              : <span className="ms-1 text-warning-text" title="This tax is added on top at checkout."> · Exclusive (+ on top)</span>
                            }
                          </span>
                        </Label>
                      ))}
                      {(Array.isArray(taxRates) ? taxRates : []).filter((tx: TaxRateRow) => tx.isActive !== false).length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-2">
                          No tax rates defined. Create them in{' '}
                          {canOpenTaxRates
                            ? <Link to={TAX_RATES_PATH} className="font-medium text-primary-text underline">Tax Rates page</Link>
                            : 'Tax Rates page'}.
                        </p>
                      )}
                    </div>
                  )}
                  {/* POS-I111: the legacy single tax rate is charged when no product/category rate applies;
                      show it so the owner can see where a tax comes from and remove it. */}
                  {form.legacyTaxRate > 0 && (
                    <div className="mt-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs">
                      {form.clearLegacyTax ? (
                        <p className="text-muted-foreground">{t('legacyTaxRemoved', { rate: form.legacyTaxRate })}</p>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-warning-text flex-1 min-w-[12rem]">{t('legacyTaxNotice', { rate: form.legacyTaxRate })}</p>
                          <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, clearLegacyTax: true })}>
                            {t('legacyTaxRemove')}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ICT Service Tax flag — only relevant when tenant has tax management or restaurant addon */}
              {(hasPermission('tax-rates:*') || hasPermission('restaurant:*')) && (
                <Label className="flex items-start gap-3 cursor-pointer rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
                  <Checkbox
                    checked={form.skipPaymentMethodTax}
                    onChange={(e) => setForm({ ...form, skipPaymentMethodTax: e.target.checked })}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-sm font-medium">Keep own GST — skip ICT service tax</span>
                    <p className="text-xs text-muted-foreground mt-0.5">For packaged goods (Pepsi, bottled water) sold in ICT restaurants that already have GST embedded in MRP</p>
                  </div>
                </Label>
              )}
            </div>
          )}

          {/* ── TAB 3: STOCK ── */}
          {formTab === 'stock' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>
                    {editProduct ? 'Stock Quantity (read-only)' : 'Initial Stock Quantity'}
                  </Label>
                  <Input
                    type="number"
                    value={form.stockQuantity}
                    onChange={(e) => setForm({ ...form, stockQuantity: e.target.value })}
                    placeholder="0"
                    min={0}
                    step={1}
                    disabled={!!editProduct}
                    className={editProduct ? 'opacity-60 bg-muted cursor-not-allowed' : ''}
                  />
                  {editProduct && (
                    <p className="mt-1 text-xs text-muted-foreground">Adjust stock via Inventory → Adjust Stock</p>
                  )}
                  {!editProduct && form.trackStock && openingValue > 0 && (
                    <p className="mt-1 text-xs text-muted-foreground" data-testid="opening-helper">
                      {tInv('openingStockHelper', { amount: formatCurrency(openingValue) })}
                    </p>
                  )}
                  {openingNeedsConfirm && (
                    <Label className="mt-2 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 cursor-pointer">
                      <Checkbox checked={confirmOpening} onChange={(e) => setConfirmOpening(e.target.checked)} />
                      <span className="text-sm">
                        <span className="block font-medium">{tInv('confirmValue', { amount: formatCurrency(openingValue) })}</span>
                        <span className="block text-xs text-muted-foreground">{tInv('largeValueNotice', { amount: formatCurrency(openingValue) })}</span>
                      </span>
                    </Label>
                  )}
                </div>
                <div>
                  <Label>Unit of Measure (UOM)</Label>
                  <Dropdown
                    value={form.uom}
                    onChange={(e) => setForm({ ...form, uom: e.value, unit: e.value?.toLowerCase() ?? '' })}
                    options={FBR_UOM_OPTIONS.map((u) => ({ label: u.label, value: u.code }))} optionLabel="label" optionValue="value"
                    placeholder="Select UOM…"
                    className="w-full"
                    filter
                    filterPlaceholder="Search UOM…"
                  />
                </div>
              </div>

              <div className="border border-border/60 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-muted/30">
                  <span className="text-sm font-medium">Stock Alerts &amp; Reordering</span>
                </div>
                <div className="grid grid-cols-2 gap-4 p-4">
                  <div>
                    <Label>Low Stock Alert Qty</Label>
                    <Input
                      type="number"
                      value={form.lowStockAlert}
                      onChange={(e) => setForm({ ...form, lowStockAlert: e.target.value })}
                      placeholder="5"
                      min={0}
                      step={1}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">Warning badge when stock ≤ this</p>
                  </div>
                  <div>
                    <Label>Reorder Point</Label>
                    <Input
                      type="number"
                      value={form.reorderPoint}
                      onChange={(e) => setForm({ ...form, reorderPoint: e.target.value })}
                      placeholder="0"
                      min={0}
                      step={1}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">Trigger reorder alert at this level</p>
                  </div>
                  <div>
                    <Label>Reorder Quantity</Label>
                    <Input
                      type="number"
                      value={form.reorderQuantity}
                      onChange={(e) => setForm({ ...form, reorderQuantity: e.target.value })}
                      placeholder="0"
                      min={0}
                      step={1}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">Units to order when triggered</p>
                  </div>
                  <div>
                    <Label>Lead Time (days)</Label>
                    <Input
                      type="number"
                      value={form.leadTimeDays}
                      onChange={(e) => setForm({ ...form, leadTimeDays: e.target.value })}
                      placeholder="0"
                      min={0}
                      step={1}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">Expected supplier delivery time</p>
                  </div>
                </div>
              </div>

              {/* Initial serial numbers — only on create, serial-tracked, with stock qty */}
              {!editProduct && form.serialTracking && Number(form.stockQuantity) > 0 && (() => {
                const qty = Number(form.stockQuantity);
                const parsed = initialSerials.split('\n').map((s) => s.trim()).filter(Boolean);
                return (
                  <div className="space-y-1.5 rounded-lg border border-info/30 bg-info/10 p-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-info-text">Initial Serial / IMEI Numbers</Label>
                      <span className={`text-xs font-medium ${parsed.length === qty ? 'text-success-text' : 'text-warning-text'}`}>
                        {parsed.length} of {qty} entered
                      </span>
                    </div>
                    <Textarea
                      className="min-h-[120px] font-mono resize-y"
                      placeholder={`Enter ${qty} serial number(s), one per line\ne.g.\nSN001234\nSN001235`}
                      value={initialSerials}
                      onChange={(e) => setInitialSerials(e.target.value)}
                    />
                    {parsed.length > 0 && parsed.length !== qty && (
                      <p className="text-xs text-warning-text">Count must match stock quantity ({qty}) before saving.</p>
                    )}
                    {parsed.length === qty && (
                      <p className="text-xs text-success-text">✓ All {qty} serial numbers entered.</p>
                    )}
                  </div>
                );
              })()}

              {!editProduct && form.serialTracking && Number(form.stockQuantity) === 0 && (
                <p className="text-xs text-muted-foreground rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
                  Set initial stock quantity above to enter serial numbers.
                </p>
              )}
            </div>
          )}

          {/* ── TAB 4: FBR ── */}
          {formTab === 'fbr' && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">HS / PCT Code</Label>
                <p className="text-xs text-muted-foreground mb-1.5">Pakistan Customs Tariff code — required for FBR Digital Invoice submission.</p>
                <div className="relative" ref={hsDropdownRef}>
                  <Input
                    dir="ltr"
                    value={form.hsCode}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm({ ...form, hsCode: val });
                      setHsSearchTerm(val);
                      setHsLookupResult(null);
                    }}
                    onFocus={() => { if (hsResults.length > 0) setHsDropdownOpen(true); }}
                    placeholder="Type to search (e.g. 3305 or toothpaste)"
                    className="text-sm font-mono"
                  />
                  {hsDropdownOpen && hsResults.length > 0 && (
                    <div className="absolute z-50 top-full inset-x-0 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-background shadow-lg">
                      {hsResults.map((r) => (
                        <button
                          key={r.code}
                          type="button"
                          className="w-full text-start px-3 py-2 hover:bg-accent/50 border-b border-border/30 last:border-0"
                          onMouseDown={() => {
                            setForm({ ...form, hsCode: r.code });
                            setHsSearchTerm('');
                            setHsDropdownOpen(false);
                            setHsResults([]);
                            setHsLookupResult(null);
                          }}
                        >
                          <span className="font-mono text-xs font-semibold text-primary-text">{r.code}</span>
                          <span className="ms-2 text-xs text-muted-foreground truncate">{r.description}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {canFbrLookup && form.hsCode && form.hsCode.length >= 4 && (
                  <div className="mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 gap-1"
                      disabled={hsLookupLoading}
                      onClick={async () => {
                        setHsLookupLoading(true);
                        setHsLookupResult(null);
                        try {
                          const res = await api.get(`/fbr/hs-lookup?hs_code=${encodeURIComponent(form.hsCode)}`);
                          setHsLookupResult(res.data);
                        } catch {
                          setHsLookupResult({ valid: false, isStandard18: false, validUoms: [], description: null, message: 'FBR lookup request failed. Check your network or FBR token.' });
                        } finally {
                          setHsLookupLoading(false);
                        }
                      }}
                    >
                      {hsLookupLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
                      Verify with FBR
                    </Button>
                    {hsLookupResult && (
                      <div className={`mt-2 rounded-md border px-3 py-2 text-xs flex items-start gap-2 ${
                        hsLookupResult.valid
                          ? hsLookupResult.isStandard18
                            ? 'bg-success/10 border-success/30'
                            : 'bg-warning/10 border-warning/30'
                          : 'bg-destructive/10 border-destructive/30'
                      }`}>
                        {hsLookupResult.valid
                          ? <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-success-text" />
                          : <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-destructive-text" />
                        }
                        <div>
                          {hsLookupResult.description && <p className="font-medium mb-0.5">{hsLookupResult.description}</p>}
                          <p className={hsLookupResult.valid ? (hsLookupResult.isStandard18 ? 'text-success-text' : 'text-warning-text') : 'text-destructive-text'}>
                            {hsLookupResult.message}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
                Additional FBR fields (Scenario, SRO, Fixed Retail Price) are configured in the FBR module after the product is created.
              </p>
            </div>
          )}
        </div>
        </form>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title={t('deleteProduct')}
        message={`Are you sure you want to delete "${deleteConfirm.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate(deleteConfirm.id, {
            onSettled: () => setDeleteConfirm({ open: false, id: '', name: '' }),
          });
        }}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />

      {/* T-112: Disable stock-tracking confirmation (replaces former window.confirm) */}
      <ConfirmDialog
        open={disableTrackStockConfirm.open}
        variant="warning"
        title="Disable Stock Tracking?"
        message={`This product has ${disableTrackStockConfirm.stockQuantity} units in stock. Disabling stock tracking will stop all inventory operations for this product. You can re-enable it later, but historical stock movements will not be replayed. Continue?`}
        confirmLabel="Yes, Disable Tracking"
        cancelLabel="Keep Tracking"
        onConfirm={() => {
          setForm((prev) => ({ ...prev, trackStock: false }));
          setDisableTrackStockConfirm({ open: false, stockQuantity: 0 });
        }}
        onCancel={() => setDisableTrackStockConfirm({ open: false, stockQuantity: 0 })}
      />

      {/* Serial Number Viewer Dialog */}
      <Dialog
        visible={!!serialViewProduct}
        onHide={() => setSerialViewProduct(null)}
        header={
          <div>
            <p className="font-semibold">{serialViewProduct?.name} — Serial Numbers</p>
            {serialViewData?.counts && (
              <div className="flex gap-3 mt-1 text-xs font-normal">
                {Object.entries(serialViewData.counts as Record<string, number>).map(([status, count]) => (
                  <span key={status} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
                    status === 'AVAILABLE' ? 'bg-success/10 text-success-text' :
                    status === 'SOLD' ? 'bg-info/10 text-info-text' :
                    status === 'RETURNED' ? 'bg-warning/10 text-warning-text' :
                    'bg-destructive/10 text-destructive-text'
                  }`}>
                    {status}: {count as number}
                  </span>
                ))}
              </div>
            )}
          </div>
        }
        modal
        style={{ width: '44rem', maxWidth: '100%' }}
        breakpoints={{ '960px': '85vw', '640px': '95vw' }}
      >
        <div className="space-y-3">
          {/* Filters */}
          <div className="flex gap-2">
            <Input
              dir="ltr"
              type="text"
              placeholder="Search serial number…"
              value={serialSearch}
              onChange={(e) => { setSerialSearch(e.target.value); setSerialPage(1); }}
              className="flex-1 h-9"
            />
            <Dropdown
              value={serialStatusFilter}
              onChange={(e) => { setSerialStatusFilter(e.value); setSerialPage(1); }}
              options={[
                { label: 'All statuses', value: '' },
                { label: 'Available', value: 'AVAILABLE' },
                { label: 'Sold', value: 'SOLD' },
                { label: 'Returned', value: 'RETURNED' },
                { label: 'Damaged', value: 'DAMAGED' },
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder="All statuses"
            />
          </div>

          {/* Table */}
          {serialViewLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">Loading…</div>
          ) : serialViewData?.data?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground text-sm gap-2">
              <Hash className="h-8 w-8 opacity-30" />
              <p>No serial numbers found</p>
              <p className="text-xs">Serial numbers are added when adding the product or when receiving a purchase order.</p>
            </div>
          ) : (
            <div className="rounded-lg border overflow-hidden">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('tbl.serialImei')}</Th>
                    <Th className="text-center">{t('tbl.status')}</Th>
                    <Th>{t('tbl.notes')}</Th>
                    <Th className="text-end">{t('tbl.added')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {serialViewData?.data?.map((sn: SerialNumberRow) => (
                    <Tr key={sn.id}>
                      <Td label={t('tbl.serialImei')} className="font-mono text-sm font-medium">{sn.serialNo}</Td>
                      <Td label={t('tbl.status')} className="text-center">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          sn.status === 'AVAILABLE' ? 'bg-success/10 text-success-text' :
                          sn.status === 'SOLD' ? 'bg-info/10 text-info-text' :
                          sn.status === 'RETURNED' ? 'bg-warning/10 text-warning-text' :
                          'bg-destructive/10 text-destructive-text'
                        }`}>
                          {sn.status}
                        </span>
                      </Td>
                      <Td label={t('tbl.notes')} className="text-xs text-muted-foreground">{sn.notes ?? '—'}</Td>
                      <Td label={t('tbl.added')} className="text-end text-xs text-muted-foreground">
                        {new Date(sn.createdAt).toLocaleDateString()}
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {serialViewData && serialViewData.total > 50 && (
            <PaginationBar
              page={serialPage}
              total={serialViewData.total}
              pageSize={50}
              onPageChange={setSerialPage}
            />
          )}

          <p className="text-xs text-muted-foreground">
            Total: {serialViewData?.total ?? 0} serial number(s) across all modes (test + production).
          </p>
        </div>
      </Dialog>

      {/* ── Bulk Price Adjust Modal ─────────────────────────────── */}
      <Dialog
        visible={bulkPriceModalOpen}
        onHide={() => setBulkPriceModalOpen(false)}
        header={
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-primary-text" />
            <span>Adjust Sale Prices — {bulkPriceRows.length} product(s)</span>
          </div>
        }
        modal
        style={{ width: '54rem', maxWidth: '100%' }}
        breakpoints={{ '960px': '90vw', '640px': '98vw' }}
      >
        {(() => {
          const changedCount = bulkPriceRows.filter((r) => {
            const v = parseFloat(r.newSalePrice);
            return !isNaN(v) && v > 0 && v !== r.salePrice;
          }).length;
          const hasErrors = bulkPriceRows.some((r) => {
            const v = parseFloat(r.newSalePrice);
            return isNaN(v) || v <= 0;
          });

          return (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="px-3 py-2 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground">Product</th>
                      <th className="px-3 py-2 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground">SKU</th>
                      <th className="px-3 py-2 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cost</th>
                      <th className="px-3 py-2 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Current Price</th>
                      <th className="px-3 py-2 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground w-40">New Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {bulkPriceRows.map((row) => {
                      const newPrice = parseFloat(row.newSalePrice);
                      const isValid = !isNaN(newPrice) && newPrice > 0;
                      const isChanged = isValid && newPrice !== row.salePrice;
                      return (
                        <tr key={row.id} className={isChanged ? 'bg-info/5' : ''}>
                          <td className="px-3 py-2 font-medium truncate max-w-[180px]">{row.name}</td>
                          <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{row.sku || '—'}</td>
                          <td className="px-3 py-2 text-end text-muted-foreground">{formatCurrency(row.costPrice)}</td>
                          <td className="px-3 py-2 text-end">
                            {isChanged ? (
                              <span className="line-through text-muted-foreground">{formatCurrency(row.salePrice)}</span>
                            ) : (
                              formatCurrency(row.salePrice)
                            )}
                          </td>
                          <td className="px-3 py-2 text-end">
                            <Input
                              type="number"
                              min={0.01}
                              step={0.01}
                              value={row.newSalePrice}
                              onChange={(e) =>
                                setBulkPriceRows((prev) =>
                                  prev.map((r) => r.id === row.id ? { ...r, newSalePrice: e.target.value } : r)
                                )
                              }
                              className={`text-end w-32 ms-auto h-8 text-sm ${!isValid ? 'border-destructive' : isChanged ? 'border-info/30' : ''}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between pt-1">
                <span className="text-sm text-muted-foreground">
                  {changedCount > 0
                    ? <span className="text-info-text font-medium">{changedCount} price(s) will be updated</span>
                    : 'Edit the "New Price" column to apply changes'}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => {
                    setBulkPriceRows((prev) => prev.map((r) => ({ ...r, newSalePrice: String(r.salePrice) })));
                  }}>
                    <RotateCcw className="h-3.5 w-3.5 me-1.5" />Reset
                  </Button>
                  <Button
                    size="sm"
                    disabled={changedCount === 0 || hasErrors || bulkPriceAdjustMutation.isPending}
                    onClick={() => {
                      const updates = bulkPriceRows
                        .filter((r) => { const v = parseFloat(r.newSalePrice); return !isNaN(v) && v > 0 && v !== r.salePrice; })
                        .map((r) => ({ id: r.id, salePrice: parseFloat(r.newSalePrice) }));
                      bulkPriceAdjustMutation.mutate(updates);
                    }}
                  >
                    <Save className="h-3.5 w-3.5 me-1.5" />
                    {bulkPriceAdjustMutation.isPending ? 'Saving…' : `Save ${changedCount} Change(s)`}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}
      </Dialog>
      
      </>)}
    </div>
  );
}
